/**
 * GameState - Centralized Game State Container
 *
 * Consolidates all global game state into a single, organized class.
 * This replaces the scattered global variables in client.js with a
 * structured state container that's easier to manage, test, and debug.
 *
 * Categories:
 * - Connection: Socket and room state
 * - Entities: Players, projectiles, world objects
 * - Map: Dimensions and configuration
 * - Input: Keyboard, mouse, gamepad state
 * - Timing: Game loop timing
 * - Effects: Visual effects and UI state
 * - Config: Loaded configuration objects
 */

import { Movement } from '../../shared/Movement.js';
import { lerpBounds } from '../../shared/Storm.js';
import { NetStats } from './NetStats.js';
import { MINIMAP_DEFAULTS, isExpiredShot } from '../rendering/pixi/hud/minimapMath.js';

class GameState {
  constructor() {
    // ─────────────────────────────────────────────────────────────
    // CONNECTION STATE
    // ─────────────────────────────────────────────────────────────
    this.localId = null;
    this.isInGame = false;
    this.playerName = 'Player';
    this.currentRoomId = null;
    this.roomState = null;

    // ─────────────────────────────────────────────────────────────
    // ENTITY COLLECTIONS
    // ─────────────────────────────────────────────────────────────
    this.players = new Map();
    // Decoys are RemotePlayers drawn like anyone else, but they are not people:
    // they never reach the scoreboard, the leaderboard or any per-player lookup.
    this.decoys = new Map();      // id → RemotePlayer, replaced wholesale by each snapshot
    // decoyId -> performance.now() of the shot that killed it: the renderer plays
    // the player death beat for these instead of just dropping the sprite.
    this.recentDecoyShots = new Map();
    this.shotStreaks = [];
    this.predictedImpacts = [];
    this.hitregMarks = [];
    this.hitregDebugActive = false;
    this.scoreboardPlayers = new Map();
    this.projectiles = [];          // Server-confirmed projectiles
    this.predictedProjectiles = []; // Client-predicted projectiles
    this.visibleProjectiles = [];   // Published each update tick by core/ProjectileSim; renderer reads only
    this.worldObjects = new Map();  // Server-authoritative world objects
    this.lootContainers = new Map(); // Serialized loot containers for interaction tracking
    this.worldObjectsVersion = 0;    // Bumped by rebuildColliders(); the minimap redraws its static layer on change

    // Team / mode state (Phase 3B). null in non-team modes.
    this.team = null;        // local player's team index
    this.teams = null;       // [{ id, name }] from the spawn payload
    this.modeState = null;   // last snapshot.mode (e.g. TDM teamScores)
    this.alivePlayers = null;   // Authoritative remaining-player count from server; null until first snapshot

    // ─────────────────────────────────────────────────────────────
    // MAP STATE
    // ─────────────────────────────────────────────────────────────
    this.mapWidth = 4000;   // Default fallback
    this.mapHeight = 2000;  // Default fallback
    this.mapConfig = null;  // Full map config from server
    this.currentMapPath = null; // Current loaded map path

    // Movement colliders built from worldObjects (same function as the server)
    this.colliders = null;
    // Server clock estimate: serverTime ≈ Date.now() + clockOffset.
    // Kept as the MAX of a window of (serverTime − Date.now()) samples: the least-delayed
    // snapshot is the truest, and a late one then cannot drag the estimate (F5).
    this.clockOffset = null;
    this.clockSamples = [];
    // Connection quality: RTT, snapshot jitter, prediction corrections, adaptive interpolation delay.
    this.netStats = new NetStats({ defaultInterpolationMs: GameState.INTERPOLATION_DELAY, maxInterpolationMs: GameState.MAX_INTERPOLATION_DELAY });

    // ─────────────────────────────────────────────────────────────
    // RECONNECT / REJOIN
    // ─────────────────────────────────────────────────────────────
    this.lastJoinPayload = null; // the setPlayerName payload, replayed to rejoin
    this.rejoinPending = false;  // awaiting rejoin/rejoinFailed after a drop
    this.rejoinTimer = null;     // fallback-to-lobby timer

    // ─────────────────────────────────────────────────────────────
    // INPUT STATE
    // ─────────────────────────────────────────────────────────────
    this.keys = {};
    this.inputState = {
      up: false,
      down: false,
      left: false,
      right: false,
      mouse: { x: 0, y: 0, down: false },
      angle: 0,
      lastAimSend: 0,
      aimSeq: 0
    };
    // Track last aim source so we can persist gamepad aim when stick is released
    this.inputState.lastAimSource = 'mouse'; // 'mouse' or 'gamepad'
    this.inputState.lastGamepadAngle = 0;

    // ─────────────────────────────────────────────────────────────
    // PROJECTILE TRACKING
    // ─────────────────────────────────────────────────────────────
    // Server projectile state - stores last known state for extrapolation
    // Maps projectile id -> {receiveTime, x, y, vx, vy, terminationTime}
    this.serverProjectileState = new Map();

    // Client-side weapon state for prediction (independent of server updates)
    this.clientLastShotTime = 0;

    // Track last shot time per player for animation state
    // Maps playerId -> timestamp of last shot
    this.playerLastShotTime = new Map();

    // Minimap enemy-fire markers: playerId -> { x, y, time } of that player's
    // most recent shot (muzzle position, performance.now() clock). Local shots
    // are never recorded; entries expire in expireTransients().
    this.recentShots = new Map();
    // playerId -> performance.now() of their elimination. Lets the renderer tell
    // a death from a disconnect even when the next snapshot has already removed
    // the dead player (no-respawn modes). Expires in expireTransients().
    this.recentDeaths = new Map();

    // ─────────────────────────────────────────────────────────────
    // UI STATE
    // ─────────────────────────────────────────────────────────────
    this.inventoryOpen = false;  // TAB key toggles inventory screen
    this.mapOpen = false;        // M key toggles the large map overlay
    this.leaderboardOpen = false; // L key toggles the leaderboard popup
    this.spectateTargetId = null; // set while spectating after an early elimination; who the camera follows

    // ─────────────────────────────────────────────────────────────
    // VISUAL EFFECTS STATE
    // ─────────────────────────────────────────────────────────────
    // Elimination feed and notification
    this.eliminationFeed = [];          // Array of {killer, victim, timestamp, expiresAt}
    this.eliminationNotification = null; // {victim, timestamp, expiresAt}
    this.deathNotification = null;       // {killerName, timestamp, expiresAt}

    // Floating damage numbers
    // Array of {playerId, damage, createdAt, x, y, vx, angle, vAng}
    this.floatingDamageNumbers = [];

    // Per-player toggle to alternate left/right offset for consecutive numbers
    this.damageNumberToggle = new Map();

    // Teleport trail visual effects
    // Each trail: { playerId, startX, startY, endX, endY, angle, startTime, duration, skinId }
    this.teleportTrails = [];
    this.nullifyRings = [];   // { x, y, startRadius, maxRadius, expandMs, startTime, color }
    this.trailBreaks = [];  // player ids whose movement trail must restart (teleport, rewind)

    // ─────────────────────────────────────────────────────────────
    // ABILITY STATE
    // ─────────────────────────────────────────────────────────────
    this.abilityState = {
      equipped: {
        utility: null,    // { abilityId, stacks }
        movement: null
      },
      cooldowns: {},      // abilityId -> cooldownEndTime
      globalCooldowns: {
        utility: 0,
        movement: 0
      },
      globalCooldownDuration: 3000, // Set by server
      activeEffects: {},  // abilityId -> { startTime, endTime }
      silencedUntil: 0,   // performance.now() — both ability slots locked after Nullify
    };

    // Zone state snapshot (Battle Royale)
    this.zoneState = {
      phaseIndex: 0,
      totalPhases: 0,
      damagePerTick: 0,
      tickInterval: 1000,
      timeRemaining: 0,
      shape: null,
      startShape: null,
      targetShape: null,
      phaseStartTime: 0,
      phaseEndTime: 0,
      lastUpdated: 0,
      // Storm (5C): 'hold' | 'shrink' | 'closed', stage index, and the next stage's damage.
      phase: null,
      stageIndex: null,
      nextDamagePerTick: null
    };
    this.lastZoneDamage = null;

    // Ability config cache for quick lookups during client-side gating
    this._abilityConfigCache = null;
    this._abilityConfigCacheSource = null;

    // ─────────────────────────────────────────────────────────────
    // DEBUG STATE
    // ─────────────────────────────────────────────────────────────
    this.mouseWorldX = 0;
    this.mouseWorldY = 0;
    this.debugMuzzleOffset = false;

    // ─────────────────────────────────────────────────────────────
    // LOADED CONFIGS (populated by ConfigManager)
    // ─────────────────────────────────────────────────────────────
    this.uiConfig = null;
    this.cosmeticsConfig = null;
    this.abilitiesConfig = null;

    // ─────────────────────────────────────────────────────────────
    // NOTIFICATION STATE
    // ─────────────────────────────────────────────────────────────
    // Debounce map: message -> last shown timestamp
    this.notificationDebounce = new Map();
  }

  // ─────────────────────────────────────────────────────────────
  // HELPER METHODS
  // ─────────────────────────────────────────────────────────────

  /**
   * Get the local player instance
   * @returns {LocalPlayer|null}
   */
  getLocalPlayer() {
    return this.players.get(this.localId);
  }

  /**
   * After a transport drop, is it worth replaying the join request to rejoin?
   * Only mid-match: players spawn at join time, so isInGame is already true in
   * the WAITING/COUNTDOWN warm-up, but the server drops warm-up players on
   * disconnect and a "rejoin" would just matchmake a fresh ready player. An
   * unknown room state is treated the same way (straight back to the lobby).
   * @returns {boolean}
   */
  shouldAttemptRejoin() {
    return Boolean(this.isInGame && this.lastJoinPayload && this.roomState?.state === 'in_progress');
  }

  /**
   * Check if the local player is dead
   * @returns {boolean}
   */
  isLocalPlayerDead() {
    const localPlayer = this.getLocalPlayer();
    return localPlayer && localPlayer.isDead;
  }

  /**
   * Check if local player can shoot (based on active ability restrictions)
   * @returns {true|Object} Returns true if can shoot, or the blocking ability if cannot
   */
  canLocalPlayerShoot() {
    const localPlayer = this.getLocalPlayer();
    if (!localPlayer) return false;

    const blockingAbility = this._findBlockingAbility((ability) => ability.canShoot === false, localPlayer);
    return blockingAbility || true;
  }

  /**
   * Check if local player can reload (based on active ability restrictions)
   * @returns {true|Object} Returns true if can reload, or the blocking ability if cannot
   */
  canLocalPlayerReload() {
    const localPlayer = this.getLocalPlayer();
    if (!localPlayer) return false;

    if (localPlayer.drift?.active) {
      return { id: 'drift', name: 'Drifting' };
    }

    return true;
  }

  /**
   * Reset all game state for returning to lobby
   * Called when player returns to lobby after game ends
   */
  reset() {
    // Connection state
    this.localId = null;
    this.isInGame = false;
    this.currentRoomId = null;
    this.roomState = null;

    // Entity collections
    this.players.clear();
    this.alivePlayers = null;
    this.decoys.clear();
    this.recentDecoyShots.clear();
    this.shotStreaks = [];
    this.predictedImpacts = [];
    this.hitregMarks = [];
    this.hitregDebugActive = false;
    this.scoreboardPlayers.clear();
    this.projectiles = [];
    this.predictedProjectiles = [];
    // Note: worldObjects (and the colliders built from it) are kept as they're
    // loaded on spawn; the next spawn rebuilds the colliders anyway.
    this.lootContainers = new Map();
    // A fresh server-clock estimate on rejoin; the first snapshot snaps it.
    this.clockOffset = null;
    this.clockSamples = [];
    this.netStats.reset();

    // Back in the lobby: no match to rejoin
    this.lastJoinPayload = null;
    this.rejoinPending = false;
    clearTimeout(this.rejoinTimer);
    this.rejoinTimer = null;

    this.nearestLootContainerId = null;
    this.nearestLootContainerDistance = null;
    this.lootContainerInteraction = {
      activeContainerId: null,
      progress: 0,
      holdDurationMs: 0,
      isHolding: false
    };

    // Projectile tracking
    this.serverProjectileState.clear();
    this.clientLastShotTime = 0;
    this.playerLastShotTime.clear();
    this.recentShots.clear();
    this.recentDeaths.clear();

    // UI overlays
    this.inventoryOpen = false;
    this.mapOpen = false;
    this.leaderboardOpen = false;
    this.spectateTargetId = null;

    // Visual effects
    this.eliminationFeed = [];
    this.eliminationNotification = null;
    this.deathNotification = null;
    this.floatingDamageNumbers = [];
    this.damageNumberToggle.clear();
    this.teleportTrails = [];
    this.nullifyRings = [];
    this.trailBreaks = [];  // player ids whose movement trail must restart (teleport, rewind)

    // Ability state
    this.abilityState = {
      equipped: { utility: null, movement: null },
      cooldowns: {},
      globalCooldowns: { utility: 0, movement: 0 },
      globalCooldownDuration: 3000,
      activeEffects: {},
      silencedUntil: 0
    };
    this.zoneState = {
      phaseIndex: 0,
      totalPhases: 0,
      damagePerTick: 0,
      tickInterval: 1000,
      timeRemaining: 0,
      shape: null,
      startShape: null,
      targetShape: null,
      phaseStartTime: 0,
      phaseEndTime: 0,
      lastUpdated: 0,
      // Storm (5C): 'hold' | 'shrink' | 'closed', stage index, and the next stage's damage.
      phase: null,
      stageIndex: null,
      nextDamagePerTick: null
    };
    this.lastZoneDamage = null;

    // Input state
    this.inputState.up = false;
    this.inputState.down = false;
    this.inputState.left = false;
    this.inputState.right = false;
    this.inputState.mouse.down = false;

    // Clear keys
    for (const key in this.keys) {
      this.keys[key] = false;
    }
  }

  /**
   * Clear all pressed keys
   * Called when window loses focus or tab becomes hidden
   */
  clearAllKeys() {
    for (const key in this.keys) {
      this.keys[key] = false;
    }
  }

  /**
   * Update or insert a scoreboard entry for a player
   * @param {string} playerId
   * @param {Object} data
   */
  updateScoreboardPlayer(playerId, data = {}) {
    if (!playerId) return;
    const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
    let entry = this.scoreboardPlayers.get(playerId);
    if (!entry) {
      entry = {
        id: playerId,
        name: data.name || data.username || 'Player',
        kills: typeof data.kills === 'number' ? data.kills : 0,
        deaths: typeof data.deaths === 'number' ? data.deaths : 0,
        team: Number.isInteger(data.team) ? data.team : null,
        isLocal: playerId === this.localId,
        lastSeen: now
      };
      this.scoreboardPlayers.set(playerId, entry);
    } else {
      if (data.name || data.username) {
        entry.name = data.name || data.username || entry.name;
      }
      if (typeof data.kills === 'number') {
        entry.kills = data.kills;
      }
      if (typeof data.deaths === 'number') {
        entry.deaths = data.deaths;
      }
      if (Number.isInteger(data.team)) {
        entry.team = data.team;
      }
      entry.isLocal = playerId === this.localId;
      entry.lastSeen = now;
    }
  }

  /**
   * Get scoreboard entries (filters out stale ones)
   * @param {number} maxAgeMs
   * @returns {Array}
   */
  /**
   * Record another player's shot for the minimap's enemy-fire marker.
   * @param {string} ownerId - shooter id
   * @param {number} x - muzzle world x
   * @param {number} y - muzzle world y
   * @param {number} nowMs - performance.now() clock
   * @returns {boolean} true when stored (false for the local player or a missing id)
   */
  recordShot(ownerId, x, y, nowMs) {
    if (!ownerId || ownerId === this.localId) return false;
    this.recentShots.set(ownerId, { x, y, time: nowMs });
    return true;
  }

  /**
   * Record that a player was just eliminated (performance.now() clock).
   * @param {string} playerId
   * @param {number} nowMs
   */
  recordDeath(playerId, nowMs) {
    if (playerId) this.recentDeaths.set(playerId, nowMs);
  }

  /**
   * Record that an enemy shot a decoy (performance.now() clock), so it dies
   * on screen like a player. Expires with recentDeaths.
   * @param {string} decoyId
   * @param {number} nowMs
   */
  recordDecoyShot(decoyId, nowMs) {
    if (decoyId) this.recentDecoyShots.set(decoyId, nowMs);
  }

  /** How long an enemy's fire marker lingers after their last shot (ms). */
  shotMarkerMs() {
    return this.uiConfig?.minimap?.shotMarkerMs ?? MINIMAP_DEFAULTS.shotMarkerMs;
  }

  /**
   * Drop time-limited HUD state that has expired. Called from the client update
   * tick; renderers read gameState but never write it (Phase 3D).
   *
   * Two clocks, on purpose: hit indicators are stamped with Date.now(), shot
   * markers with performance.now() (like playerLastShotTime). Pass both.
   * @param {number} nowMs - Date.now() clock
   * @param {number} perfNowMs - performance.now() clock
   */
  expireTransients(nowMs = Date.now(), perfNowMs = performance.now()) {
    if (Array.isArray(this.offScreenHitIndicators) && this.offScreenHitIndicators.length) {
      this.offScreenHitIndicators = this.offScreenHitIndicators.filter((hit) => nowMs - hit.timestamp < hit.duration);
    }
    this.shotStreaks = this.shotStreaks.filter((s) => perfNowMs - s.startTime < GameState.SHOT_STREAK_MS);
    const impactLife = this.hitregDebugActive ? GameState.HITREG_MARK_MS : GameState.PREDICTED_IMPACT_MS;
    this.predictedImpacts = this.predictedImpacts.filter((p) => perfNowMs - p.startTime < impactLife);
    this.hitregMarks = this.hitregMarks.filter((m) => perfNowMs - m.startTime < GameState.HITREG_MARK_MS);
    if (this.recentShots.size) {
      const windowMs = this.shotMarkerMs();
      for (const [id, shot] of this.recentShots) {
        if (isExpiredShot(shot.time, perfNowMs, windowMs)) this.recentShots.delete(id);
      }
    }
    for (const [id, time] of this.recentDeaths) {
      if (perfNowMs - time >= GameState.RECENT_DEATH_MS) this.recentDeaths.delete(id);
    }
    for (const [id, time] of this.recentDecoyShots) {
      if (perfNowMs - time >= GameState.RECENT_DEATH_MS) this.recentDecoyShots.delete(id);
    }
  }

  getScoreboardEntries(maxAgeMs = 120000) {
    const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
    if (maxAgeMs > 0) {
      for (const [playerId, entry] of this.scoreboardPlayers.entries()) {
        const tooOld = (now - entry.lastSeen) > maxAgeMs;
        if (tooOld && !this.players.has(playerId)) {
          this.scoreboardPlayers.delete(playerId);
        }
      }
    }
    return Array.from(this.scoreboardPlayers.values());
  }

  /** Player ids whose movement trail must restart (teleport, rewind); clears the queue. */
  drainTrailBreaks() {
    const ids = this.trailBreaks;
    this.trailBreaks = [];
    return ids;
  }

  /** Colour for a team index, or null when teams are not in play. */
  teamColor(team) {
    if (!this.teams || !Number.isInteger(team)) return null;
    return GameState.TEAM_COLORS[team % GameState.TEAM_COLORS.length];
  }

  /**
   * Update zone state snapshot
   * @param {Object} zoneData
   */
  setZoneState(zoneData = {}) {
    const now = (typeof performance !== 'undefined' && performance.now)
      ? performance.now()
      : Date.now();
    if (!this.zoneState) {
      this.zoneState = {};
    }
    this.zoneState.phaseIndex = zoneData.phaseIndex ?? this.zoneState.phaseIndex ?? 0;
    this.zoneState.totalPhases = zoneData.totalPhases ?? this.zoneState.totalPhases ?? 0;
    this.zoneState.damagePerTick = zoneData.damagePerTick ?? this.zoneState.damagePerTick ?? 0;
    this.zoneState.tickInterval = zoneData.tickInterval ?? this.zoneState.tickInterval ?? 1000;
    this.zoneState.timeRemaining = zoneData.timeRemaining ?? this.zoneState.timeRemaining ?? 0;
    this.zoneState.shape = zoneData.shape ?? this.zoneState.shape ?? null;
    this.zoneState.startShape = zoneData.startShape ?? this.zoneState.startShape ?? null;
    this.zoneState.targetShape = zoneData.targetShape ?? this.zoneState.targetShape ?? null;
    this.zoneState.phaseStartTime = zoneData.phaseStartTime ?? this.zoneState.phaseStartTime ?? null;
    this.zoneState.phaseEndTime = zoneData.phaseEndTime ?? this.zoneState.phaseEndTime ?? null;
    // Storm (5C): 'hold' | 'shrink' | 'closed', stage index, and the next stage's damage.
    this.zoneState.phase = zoneData.phase ?? this.zoneState.phase ?? null;
    this.zoneState.stageIndex = zoneData.stageIndex ?? this.zoneState.stageIndex ?? this.zoneState.phaseIndex;
    this.zoneState.nextDamagePerTick = zoneData.nextDamagePerTick ?? this.zoneState.nextDamagePerTick ?? null;
    this.zoneState.lastUpdated = zoneData.lastUpdated ?? now;
  }

  /**
   * The zone as it should be drawn THIS frame. The server sends the boundary once a
   * second; between updates a shrinking zone is interpolated here from startShape →
   * targetShape over [phaseStartTime, phaseEndTime] on the server clock using
   * shared/Storm.js's lerpBounds — the bounds-space sibling of the server's
   * lerpSquare (same clamp, same linear blend; tests/shared/Storm.test.js pins them
   * equal) — so the wall glides instead of stepping and stays in lockstep with the
   * boundary that actually damages players.
   * @returns {{ shape, targetShape, phase, timeRemaining }|null}
   */
  zoneView() {
    const z = this.zoneState;
    if (!z || !z.shape) return null;
    let shape = z.shape;
    if (z.phase === 'shrink' && z.startShape?.bounds && z.targetShape?.bounds && z.phaseEndTime > z.phaseStartTime) {
      const t = (this.serverNow() - z.phaseStartTime) / (z.phaseEndTime - z.phaseStartTime);
      shape = { type: 'rect', bounds: lerpBounds(z.startShape.bounds, z.targetShape.bounds, t) };
    }
    const timeRemaining = z.phaseEndTime ? Math.max(0, z.phaseEndTime - this.serverNow()) : 0;
    return { shape, targetShape: z.targetShape, phase: z.phase, timeRemaining };
  }

  /**
   * Record latest zone damage tick (for UI feedback)
   * @param {Object} data
   */
  recordZoneDamage(data = {}) {
    const now = (typeof performance !== 'undefined' && performance.now)
      ? performance.now()
      : Date.now();
    this.lastZoneDamage = {
      ...data,
      receivedAt: now
    };
  }

  /**
   * Convenience getter for current zone shape
   * @returns {Object|null}
   */
  getZoneShape() {
    return this.zoneState?.shape || null;
  }

  _findBlockingAbility(predicate, localPlayer = null) {
    const activeIds = this._getActiveAbilityIds(localPlayer);
    if (activeIds.size === 0) {
      return null;
    }

    for (const abilityId of activeIds) {
      const abilityConfig = this._getAbilityConfig(abilityId);
      if (abilityConfig && predicate(abilityConfig)) {
        return abilityConfig;
      }
    }

    return null;
  }

  _getActiveAbilityIds(localPlayer = null) {
    const ids = new Set();

    const activeEffects = this.abilityState?.activeEffects;
    if (activeEffects) {
      for (const abilityId of Object.keys(activeEffects)) {
        ids.add(abilityId);
      }
    }

    const player = localPlayer || this.getLocalPlayer();
    if (player?.activeAbilityEffects) {
      for (const abilityId of Object.keys(player.activeAbilityEffects)) {
        ids.add(abilityId);
      }
    }

    return ids;
  }

  _getAbilityConfig(abilityId) {
    if (!abilityId) {
      return null;
    }

    if (this._abilityConfigCacheSource !== this.abilitiesConfig) {
      this._abilityConfigCache = new Map();
      const abilities = this.abilitiesConfig?.abilities || [];
      for (const ability of abilities) {
        if (ability?.id) {
          this._abilityConfigCache.set(ability.id, ability);
        }
      }
      this._abilityConfigCacheSource = this.abilitiesConfig;
    }

    return this._abilityConfigCache?.get?.(abilityId) || null;
  }

  /** Rebuild movement colliders from the current worldObjects (spawn, container state change). */
  rebuildColliders() {
    this.worldObjectsVersion++; // every world-object mutation site calls this; the minimap keys off it
    if (typeof Movement === 'undefined' || !this.mapWidth || !this.mapHeight) return;
    this.colliders = Movement.buildColliders(this.worldObjects, { width: this.mapWidth, height: this.mapHeight });
  }

  /** Update the server-clock estimate from a snapshot (max-filtered over a window). */
  observeServerTime(serverTime, recvTime = null) {
    if (typeof serverTime !== 'number') return;
    const sample = serverTime - Date.now();
    this.clockSamples.push(sample);
    if (this.clockSamples.length > GameState.CLOCK_WINDOW) this.clockSamples.shift();
    // Max of the window = the snapshot that arrived with the least delay.
    let best = -Infinity;
    for (const s of this.clockSamples) if (s > best) best = s;
    this.clockOffset = best;
    if (recvTime !== null) this.netStats.onSnapshot(serverTime, recvTime);
  }

  /** How far behind the server clock remote players are rendered right now (adaptive, see NetStats). */
  interpolationDelay() {
    return this.netStats.interpolationDelayMs();
  }

  /** Best estimate of the server's Date.now(). */
  serverNow() {
    return Date.now() + (this.clockOffset ?? 0);
  }
}

// ─────────────────────────────────────────────────────────────
// CONSTANTS (could be moved to config files later)
// ─────────────────────────────────────────────────────────────
GameState.DEBUG_MODE = true;

// Timing constants
GameState.FIXED_DT = 1 / 60;

// Interpolation delay in milliseconds - renders entities this far in the past.
// This is the DEFAULT before the connection has been measured; NetStats then adapts it
// between 2 broadcast intervals and MAX_INTERPOLATION_DELAY from observed jitter.
GameState.INTERPOLATION_DELAY = 100;
GameState.MAX_INTERPOLATION_DELAY = 150;
// Server-clock estimate window (snapshots): ~1 s at 30 Hz.
GameState.CLOCK_WINDOW = 30;

// Reconciliation smoothing - prevents snapping on small corrections
// With server and client running the same exact movement model, any error >= 2px
// is a genuine correction (teleport, container opened under us, ability timing).
// Visual smoothing of corrections lives in LocalPlayer (RENDER_OFFSET_DECAY / RENDER_SNAP_DISTANCE).
GameState.RECONCILE_THRESHOLD = 2;        // Correct the simulation if error > this many pixels (below: float noise)
GameState.RECONCILE_SMOOTH_FACTOR = 0;    // Unused since 5B: the picture is smoothed by LocalPlayer.visualOffset, not the simulation

// Floating damage number constants
GameState.DAMAGE_NUMBER_DURATION = 800;      // ms - how long damage numbers float
GameState.DAMAGE_NUMBER_FLOAT_SPEED = 30;    // pixels per second upward
GameState.DAMAGE_NUMBER_FADE_START = 0.4;    // Start fading at 40% of duration
GameState.DAMAGE_NUMBER_HORZ_MIN = 20;       // Min horizontal drift (px/sec)
GameState.DAMAGE_NUMBER_HORZ_MAX = 40;       // Max horizontal drift (px/sec)

// Notification constants
GameState.NOTIFICATION_DEBOUNCE_MS = 3000;

// Decoy constants
GameState.SHOT_STREAK_MS = 150;
GameState.PREDICTED_IMPACT_MS = 180;
GameState.HITREG_MARK_MS = 1500;
GameState.RECENT_DEATH_MS = 2000;            // how long a death still counts for the death beat

// Projectile constants
GameState.PROJECTILE_LIFETIME = 3000;        // ms - should match server config

// Movement params — populated from the server's spawn payload (playerConfig.movement).
// Defaults mirror config/game.json and are used only until spawn arrives.
GameState.MOVEMENT = {
  speedPxPerSec: 378,
  radius: 40
};

// Drift (hold-to-slide) tuning — per-tick params from the server's playerConfig.drift on spawn.
GameState.DRIFT = Movement.driftParams();

// Team colours — Red, Blue, Green, Yellow — matches BaseGameMode.teamName order
GameState.TEAM_COLORS = [0xe74c3c, 0x3498db, 0x2ecc71, 0xf1c40f];

// Export for use in other modules (when using ES modules)
// For now, GameState is a global class loaded via script tag

export { GameState };
